// src/utils/liquidMotion.js
// Shared "liquid" motion primitives — a viscous spring + an easing curve that
// decelerates like something settling into a fluid, plus a click-point ripple
// hook. Reused by AnimatedButton, toasts, and page transitions so the whole
// app shares one motion language instead of every component inventing its own.
import React, { useCallback, useState } from "react";
import { motion } from "framer-motion";

// Slight overshoot + settle, like a drop of liquid wobbling into its shape.
// Softer stiffness + slightly heavier mass than a typical UI spring — reads
// as viscous rather than snappy.
export const LIQUID_SPRING = { type: "spring", stiffness: 260, damping: 24, mass: 0.9 };

// Smooth, weighted deceleration for non-spring (duration-based) transitions.
export const LIQUID_EASE = [0.22, 1, 0.36, 1];

let rippleId = 1;

/**
 * useRipple — tracks a set of expanding-circle ripples anchored at the exact
 * point a pointer event landed, each self-removing after its animation ends.
 * The host element needs `position: relative; overflow: hidden` so ripples
 * stay clipped to its shape (e.g. a rounded button).
 */
export function useRipple() {
  const [ripples, setRipples] = useState([]);

  const addRipple = useCallback((e) => {
    const target = e.currentTarget;
    if (!target || typeof target.getBoundingClientRect !== "function") return;
    const rect = target.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height) * 2.2;
    const clientX = e.clientX ?? rect.left + rect.width / 2;
    const clientY = e.clientY ?? rect.top + rect.height / 2;
    const x = clientX - rect.left - size / 2;
    const y = clientY - rect.top - size / 2;
    const id = rippleId++;

    setRipples((prev) => [...prev, { id, x, y, size }]);
    setTimeout(() => {
      setRipples((prev) => prev.filter((r) => r.id !== id));
    }, 700);
  }, []);

  return { ripples, addRipple };
}

/**
 * RippleOverlay — renders whatever `useRipple()` is currently tracking.
 * Drop inside any element with `position: relative; overflow: hidden` and
 * wire `onPointerDown={addRipple}` on that same element.
 */
export function RippleOverlay({ ripples }) {
  return (
    <>
      {ripples.map((r) => (
        <motion.span
          key={r.id}
          initial={{ opacity: 0.4, scale: 0 }}
          animate={{ opacity: 0, scale: 1 }}
          transition={{ duration: 0.65, ease: "easeOut" }}
          style={{
            position: "absolute",
            left: r.x,
            top: r.y,
            width: r.size,
            height: r.size,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.5)",
            pointerEvents: "none",
          }}
        />
      ))}
    </>
  );
}
